import React, { ChangeEvent, useState } from "react";
import { ParseResult } from "papaparse";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwCsvReader from "../../../shared/OwCsvReader/OwCsvReader";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import { IInventoryResetDTO } from "../../../../models/DTO/IInventoryResetDTO";
import INotification from "../../../../models/INotification";

type Props = {
    closeModalFn: () => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    catalogGuid: string;
    catalogName: string;
    onSubmit: (body: IInventoryResetDTO) => void;
};

export const InventoryResetModal: React.FC<Props> = (props) => {
    const [dryRun, setDryRun] = useState<boolean>(true);
    const [createBackorderOrders, setCreateBackorderOrders] = useState<boolean>(false);
    const [batchSize, setBatchSize] = useState<number>(100);
    const [useCsvData, setUseCsvData] = useState<boolean>(false);
    const [csvData, setCsvData] = useState<{ sku: string; qty_on_hand: number }[] | undefined>();
    const [csvFilename, setCsvFilename] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const closeModal = () => {
        props.closeModalFn();
    };

    const handleDryRunChange = (e: ChangeEvent<HTMLInputElement>) => {
        setDryRun(e.target.checked);
    };

    const handleCreateBackorderOrdersChange = (e: ChangeEvent<HTMLInputElement>) => {
        setCreateBackorderOrders(e.target.checked);
    };

    const handleBatchSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value > 0) {
            setBatchSize(value);
        }
    };

    const handleUseCsvDataChange = (e: ChangeEvent<HTMLInputElement>) => {
        setUseCsvData(e.target.checked);
        if (!e.target.checked) {
            setCsvData(undefined);
            setCsvFilename("");
        }
    };

    const handleCsvLoaded = (result: ParseResult<any>, filename: string) => {
        const rows = result.data
            .map((row: any) => ({
                sku: row["sku"] || row["SKU"] || row["Sku"] || "",
                qty_on_hand: parseInt(row["qty_on_hand"] || row["Qty On Hand"] || row["quantity"] || row["Quantity"] || "0", 10),
            }))
            .filter((row: { sku: string; qty_on_hand: number }) => row.sku && !isNaN(row.qty_on_hand));

        setCsvData(rows);
        setCsvFilename(filename);
    };

    const handleSubmit = () => {
        setIsProcessing(true);

        const payload: IInventoryResetDTO = {
            catalogGuid: props.catalogGuid,
            dryRun,
            createBackorderOrders,
            batchSize,
        };

        if (useCsvData && csvData && csvData.length > 0) {
            payload.inventoryData = csvData;
        }

        props.onSubmit(payload);
    };

    const isSubmitDisabled = useCsvData && (!csvData || csvData.length === 0);

    return (
        <div className={"InventoryResetModal p-3"} style={{ minWidth: "500px", maxWidth: "600px" }}>
            <div className={"d-flex justify-content-between align-items-start"}>
                <OwTitle leftIconName={"fad fa-eraser"} title={"reset_inventory"} tag={"h3"} />
                <div>
                    <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                </div>
            </div>

            <div className={"mt-3"}>
                <OwLabel title={"catalog"} isBold={true} className={"me-2"} />
                <span>{props.catalogName}</span>
            </div>

            <div className={"alert alert-warning mt-3 mb-0"}>
                <i className={"fas fa-exclamation-triangle me-2"} />
                <span>This action will recalculate all inventory reservations. Use Dry Run mode first to preview changes.</span>
            </div>

            <div
                className={"mt-3 p-3"}
                style={{
                    border: "1px solid var(--minor-bg-color)",
                    borderRadius: "4px",
                    backgroundColor: "var(--main-bg-color)",
                }}>
                <div className={"mb-3"}>
                    <OwCheckbox label={"dry_run_preview_mode"} name={"dryRun"} checked={dryRun} fn={handleDryRunChange} />
                    <small className={"text-muted d-block ms-4"}>
                        Preview changes without applying them. All changes will be rolled back.
                    </small>
                </div>

                <div className={"mb-3"}>
                    <OwCheckbox
                        label={"create_backorder_orders"}
                        name={"createBackorderOrders"}
                        checked={createBackorderOrders}
                        fn={handleCreateBackorderOrdersChange}
                    />
                    <small className={"text-muted d-block ms-4"}>
                        Create separate orders for items that cannot be fulfilled from available inventory.
                    </small>
                </div>

                <div className={"mb-3"}>
                    <OwLabel title={"batch_size"} className={"mb-1"} />
                    <div style={{ maxWidth: "150px" }}>
                        <OwInputWrapper
                            name={"batchSize"}
                            type={"number"}
                            value={batchSize}
                            fn={handleBatchSizeChange}
                            className={"form-control"}
                            min={1}
                            max={1000}
                        />
                    </div>
                </div>

                <div className={"pt-3"} style={{ borderTop: "1px solid var(--minor-bg-color)" }}>
                    <OwCheckbox label={"upload_inventory_csv"} name={"useCsvData"} checked={useCsvData} fn={handleUseCsvDataChange} />

                    {useCsvData && (
                        <div className={"mt-2 ms-4"}>
                            <OwCsvReader onFileLoaded={handleCsvLoaded} isProcessing={isProcessing} hasHeader={true} />
                            {csvFilename && (
                                <div className={"mt-2"}>
                                    <small className={"text-success"}>
                                        <i className={"fas fa-check me-1"} />
                                        {csvFilename} - {csvData?.length || 0} rows loaded
                                    </small>
                                </div>
                            )}
                            <small className={"text-muted d-block mt-1"}>
                                CSV must contain &apos;sku&apos; and &apos;qty_on_hand&apos; columns
                            </small>
                        </div>
                    )}
                </div>
            </div>

            <div className={"d-flex justify-content-end mt-3"}>
                <OwButton title={"cancel"} className={"btn-secondary me-2"} fn={closeModal} />
                <OwButton
                    title={dryRun ? "run_preview" : "reset_inventory"}
                    className={dryRun ? "btn-warning" : "btn-danger"}
                    leftIconClass={dryRun ? "fas fa-eye" : "fas fa-eraser"}
                    fn={handleSubmit}
                    disabled={isSubmitDisabled || isProcessing}
                    active={isProcessing}
                    disableOnActive={true}
                />
            </div>
        </div>
    );
};

export default InventoryResetModal;
